import { useState } from "react";

import { Grid, Typography } from "@mui/material";

import {
  faCalendarCheck,
  faLocationCrosshairs
} from "@fortawesome/free-solid-svg-icons";

import { ToggleButtons } from "@aclymatepackages/atoms";

import useCsvUploader from "./csvUploader";

import {
  doesFieldExist,
  createErrorObj
} from "../../helpers/components/inputs";

const fieldOptions = [
  { label: "Date", value: "date" },
  { label: "Description", value: "description" },
  { label: "Start Address", value: "from" },
  { label: "End Address", value: "to" },
  { label: "Distance (mi.)", value: "mileage" },
  { label: "Start/Stop Mileage", value: "startStop" },
  { label: "Vehicle", value: "vehicle" },
  { label: "Volume", value: "volume" },
  { label: "Unit", value: "unit" },
  { label: "Fuel Type", value: "fuelType" }
];

const requiredFields = [
  {
    label: "Date",
    description: "When did you take this drive or purchase fuel?",
    icon: faCalendarCheck
  },
  {
    label: "Distance or Fuel",
    description:
      "Either To & From OR Distance, OR Start/Stop Mileage, OR Fuel Volume & Fuel Type",
    icon: faLocationCrosshairs
  }
];

const knownFields = [
  { header: "date", field: "date" },
  { header: "trip purpose", field: "description" },
  { header: "description", field: "description" },
  { header: "start", field: "from" },
  { header: "start address", field: "from" },
  { header: "from", field: "from" },
  { header: "to", field: "to" },
  { header: "end", field: "to" },
  { header: "end address", field: "to" },
  { header: "mileage", field: "mileage" },
  { header: "distance", field: "mileage" },
  { header: "start/stop mileage", field: "startStop" },
  { header: "start/stop", field: "startStop" },
  { header: "vehicle", field: "vehicle" },
  { header: "volume", field: "volume" },
  { header: "gallons", field: "volume" },
  { header: "liters", field: "volume" },
  { header: "litres", field: "volume" },
  { header: "fuel volume", field: "volume" },
  { header: "amount", field: "volume" },
  { header: "quantity", field: "volume" },
  { header: "unit", field: "unit" },
  { header: "units", field: "unit" },
  { header: "volume unit", field: "unit" },
  { header: "unit of measure", field: "unit" },
  { header: "fuel type", field: "fuelType" },
  { header: "fueltype", field: "fuelType" },
  { header: "fuel", field: "fuelType" },
  { header: "type", field: "fuelType" },
  { header: "gas type", field: "fuelType" },
  { header: "gasoline", field: "volume" },
  { header: "diesel", field: "volume" },
  { header: "propane", field: "volume" },
  { header: "lpg", field: "volume" }
];

const fieldValidator = (headers) => {
  if (!doesFieldExist(headers, "date")) {
    return createErrorObj('Your CSV must include a "date" field');
  }

  const hasMileageData =
    (doesFieldExist(headers, "from") && doesFieldExist(headers, "to")) ||
    doesFieldExist(headers, "mileage") ||
    doesFieldExist(headers, "startStop");

  const hasFuelData =
    doesFieldExist(headers, "volume") && doesFieldExist(headers, "fuelType");

  if (!hasMileageData && !hasFuelData) {
    return createErrorObj(
      'Your CSV must include either mileage data ("To" and "From" fields, a "Distance" field or a "Start/Stop Mileage" field) OR fuel data ("Volume" and "Fuel Type" fields)'
    );
  }

  return { success: true };
};

const ToggleInput = ({ value, setValue, buttons, label }) => (
  <Grid container spacing={1} wrap="nowrap" alignItems="center">
    <Grid item>
      <Typography variant="body1">{label}</Typography>
    </Grid>
    <Grid item>
      <ToggleButtons
        value={value}
        onChange={setValue}
        buttons={buttons}
        style={{ margin: "auto" }}
      />
    </Grid>
  </Grid>
);

// All calc/trip-processing logic (ticket #430) now lives server-side, routed
// through the same Storage-upload -> task-doc -> draft -> review/confirm ->
// commit pipeline ticket #412 built for utilities_csv/office_csv. See
// functions/utils/smart-upload/draftFuelCsvChunk.js and
// migratedFuelCsvTransactions.js for the ported calc logic. vehicleOwnership/
// vehicleType stay as this upload's own two ToggleInputs (fixed for the whole
// file, no per-row picker, no entity_context) and are carried server-side via
// additionalMetadata -> the task doc's taskData.
const useFuelCsvUploader = ({ open, setOpen }) => {
  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [vehicleOwnership, setVehicleOwnership] = useState("company");
  const [vehicleType, setVehicleType] = useState("passenger");

  const mileageCsvUploaderSteps = useCsvUploader({
    knownFields,
    fieldOptions,
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    fieldValidator,
    docType: "fuel-mileage",
    title: "Upload your fuel OR mileage data",
    firstStepText:
      "We'll automatically calculate your carbon emissions for the drives and fuel purchases that you upload",
    requiredFields,
    additionalMetadata: { vehicleOwnership, vehicleType },
    successMessage: `Your fuel and mileage data were added to your account but it might take a minute for them to appear`
  });

  const toggleSteps = [
    {
      label: "Who owns this vehicle?",
      input: (
        <ToggleInput
          label="Who owns this vehicle?"
          value={vehicleOwnership}
          setValue={setVehicleOwnership}
          buttons={[
            {
              value: "company",
              name: "The Company"
            },
            {
              value: "rental",
              name: "It's a Rental"
            }
          ]}
        />
      )
    },
    {
      label: "What type of vehicle is this?",
      input: (
        <ToggleInput
          label="What type of vehicle was this fuel purchased for?"
          value={vehicleType}
          setValue={setVehicleType}
          buttons={[
            {
              value: "passenger",
              name: "Passenger"
            },
            {
              value: "commercial",
              name: "Commercial/industrial"
            }
          ]}
        />
      )
    }
  ];

  return [...toggleSteps, ...mileageCsvUploaderSteps];
};
export default useFuelCsvUploader;
